import { ArrayUtils, StringUtils, validateUwiString } from '../utils'
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { gql, useLazyQuery, useMutation } from '@apollo/client'
import type { RadioChangeEvent } from 'antd'
import { Button, Form, Input, Radio, Spin, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useDocumentTitle from '../hooks/useDocumentTitle'
import OrderFormFooter from './order-form/OrderFormFooter'
import OrderFormMain from './order-form/OrderFormMain'
import OrderFormPaymentRow from './order-form/OrderFormPaymentRow'
import OrderFormRequestRow, {
  OrderFormRequestFooter,
  OrderFormRequestInfo,
} from './order-form/OrderFormRequestRow'
import OrderFormRow from './order-form/OrderFormRow'
import OrderFormSection from './order-form/OrderFormSection'
import SubmissionDate from './order-form/OrderFormSubmissionDate'
import {
  UwiInfo,
  hasDuplicate,
  sanitizeUwis,
  showErrorModal,
  showWarningModal,
  toUwis,
  validateMessages,
} from './order-form/OrderFormUtils'
import Validators from './order-form/OrderFormValidators'

// @ts-ignore
import cc from '../assets/cc.png'

const MAX_UWIS_ALLOWED = 100
const VALIDATE_UWIS_GQL = gql`
  query ValidateUwis($uwis: [String!]!) {
    validateUwis(uwis: $uwis) {
      UWI
      Status
    }
  }
`
const CREATE_ORDER_EXT_GQL = gql`
  mutation CreateOneOrderExt($data: OrderCreateInput!) {
    createOneOrderExt(data: $data) {
      Number
      RejectedUWIs {
        UWI
        Status
      }
      Errors
    }
  }
`

const OrderForm: React.FC = () => {
  const [uwiInfos, setUwiInfos] = useState<UwiInfo[]>([{ uwi: '' }])
  const [validUwis, setValidUwis] = useState<string[]>([])
  const [paymentOption, setPaymentOption] = useState(1)

  const history = useHistory()
  const [form] = Form.useForm()

  useDocumentTitle('Tour Report Order Form')

  //**************************************************
  // #region API Hooks

  // Validate UWIs hook
  const [
    validateUwisApi,
    { loading: validateUwisLoading, data: validateUwisData, error: validateUwisError },
  ] = useLazyQuery(VALIDATE_UWIS_GQL)

  // Order submission hook
  const [
    createOrderApi,
    { loading: createOrderLoading, data: createOrderData, error: createOrderError },
  ] = useMutation(CREATE_ORDER_EXT_GQL)

  // #endregion

  //******************************
  // #region React hooks

  /**
   * Validate the UWIs after user has entered a UWI.
   */
  useEffect(() => {
    validateUwis()
  }, [validUwis])

  useEffect(() => {
    if (ArrayUtils.isEmpty(validateUwisData?.validateUwis)) {
      return
    }
    validateUwisData.validateUwis.forEach((wellInfo: any) => {
      const well = uwiInfos.find((uwiInfo) => uwiInfo.uwi === wellInfo.UWI)
      if (well) {
        switch (wellInfo.Status) {
          case 'Confidential':
          case 'Confidential Below':
            well.status = 'Confidential UWI'
            break
          case 'Not Found':
            well.status = 'UWI does not exist'
            break
        }
      }
    })
    setUwiInfos([...uwiInfos])
  }, [validateUwisData])

  useEffect(() => {
    if (validateUwisError) {
      showErrorModal(validateUwisError.message, validateUwisError.name)
      return
    }
  }, [validateUwisError])

  useEffect(() => {
    if (createOrderData?.createOneOrderExt == null) {
      return
    }
    const orderResponse = createOrderData.createOneOrderExt
    if (StringUtils.isNotEmpty(orderResponse.Errors)) {
      showErrorModal(
        <div>
          Unexpected errors:
          <br />
          {orderResponse.Errors}
        </div>
      )
      return
    }
    if (ArrayUtils.isNotEmpty(orderResponse.RejectedUWIs)) {
      const modalMessage = orderResponse.RejectedUWIs.map((uwi: any, idx: number) => (
        <li key={idx}>
          {uwi.UWI} {uwi.Status}
        </li>
      ))
      showWarningModal(
        <div>
          <ul>{modalMessage}</ul>
        </div>,
        'Rejected UWIs',
        'Acknowledge',
        () => {
          if (orderResponse.Number > 0) {
            history.push(`/order/${orderResponse.Number}`)
          }
        }
      )
      return
    }
    if (orderResponse.Number > 0) {
      history.push(`/order/${orderResponse.Number}`)
    } else {
      showErrorModal(<div>Order Number missing.</div>)
    }
  }, [createOrderData])

  useEffect(() => {
    if (createOrderError) {
      showErrorModal(createOrderError.message, createOrderError.name)
      return
    }
  }, [createOrderError])

  // #endregion

  //******************************
  // #region Event Handlers

  const uwiChangeHandler = (e: React.ChangeEvent<HTMLInputElement>, idx: number): void => {
    const value = e.target.value
    let values: string[] = []
    if (value.indexOf('\r\n') >= 0) {
      values = value.split('\r\n')
    } else if (value.indexOf('\n') >= 0) {
      values = value.split('\n')
    } else if (value.trim() !== '' && value.indexOf(' ') >= 0) {
      values = value.split(' ').filter((uwi) => uwi.trim().length > 0)
    } else {
      values = [value]
    }

    let newUwiInfos = [...uwiInfos]
    const newUwiInfosArray = values.map((uwi) => ({ uwi } as UwiInfo))
    newUwiInfos.splice(idx, 1, ...newUwiInfosArray)
    if (newUwiInfos.length > MAX_UWIS_ALLOWED) {
      newUwiInfos = newUwiInfos.slice(0, MAX_UWIS_ALLOWED)
    }

    // calculate the difference between old UWI list vs new UWI list
    const oldUwis = sanitizeUwis(toUwis(uwiInfos))
    const newUwis = sanitizeUwis(toUwis(newUwiInfos))
    const areEqual =
      oldUwis.length === newUwis.length && oldUwis.every((el, idx) => el === newUwis[idx])

    // update states
    setUwiInfos(newUwiInfos)
    form.setFieldsValue({ uwiList: toUwis(newUwiInfos) })
    // if new UWIs are valid and not equal to previous UWI list
    // then validate all UWIs
    if (newUwis.length > 0 && !areEqual) {
      setValidUwis(newUwis)
    }
  }

  const addReportHandler = (): void => {
    const newUwiInfos = [...uwiInfos]
    if (newUwiInfos.length > MAX_UWIS_ALLOWED) {
      return
    }
    newUwiInfos.splice(newUwiInfos.length, 0, { uwi: '' })
    setUwiInfos(newUwiInfos)
  }

  const removeReportHandler = (idx: number): void => {
    const newUwiInfos = [...uwiInfos]
    newUwiInfos.splice(idx, 1)
    validateUwisLocally(newUwiInfos)
    setUwiInfos(newUwiInfos)
  }

  const formFinishHandler = async (values: any) => {
    const thisUwiInfos = await validateUwis(true)
    if (thisUwiInfos.some((uwiInfos) => uwiInfos.status != null)) {
      showInvalidUwisPopup()
      return
    }

    createOrderApi({
      variables: {
        data: {
          Id: '',
          Meta: '',
          CompanyName: values.CompanyName,
          ContactName: values.ContactName,
          ContactEmail: values.ContactEmail,
          PhoneNumber: values.PhoneNumber,
          PoNumber: values.PoNumber,
          PaymentType: isCreditCard ? 'Credit Card' : 'AER Account',
          BillAccount: values.BillAccount || '',
          WellIdentifiersOnOrders: {
            createMany: {
              data: uwiInfos.map((uwiInfo) => {
                const { uwi } = uwiInfo
                return {
                  Id: '',
                  WellIdentifierId: undefined,
                  RequestedUWI: uwi,
                  LicenseNumber: '',
                  Status: undefined,
                }
              }),
            },
          },
        },
      },
    })
  }

  const formFinishFailedHandler = (errorInfo: any): void => {
    const namePath = errorInfo.errorFields[0].name
    form.getFieldInstance(namePath)?.focus()
  }

  const formResetHandler = (): void => {
    form.resetFields()
    setPaymentOption(1)
    setUwiInfos([{ uwi: '' }])
    setValidUwis([])
  }

  const formSubmitHandler = (): void => {
    form.submit()
  }

  // #endregion

  //******************************
  // #region Helpers

  const validateUwis = async (isEmpty?: boolean): Promise<UwiInfo[]> => {
    // reset all statuses
    uwiInfos.forEach((uwiInfo) => (uwiInfo.status = undefined))
    // loop and check for local errors
    validateUwisLocally(uwiInfos, isEmpty)
    // remote validation
    await validateUwisRemotely(uwiInfos)
    // update UWI's with their appropriate statuses to trigger React's render lifecycle
    const newUwiInfos = [...uwiInfos]
    setUwiInfos(newUwiInfos)
    return newUwiInfos
  }

  const validateUwisLocally = (uwiInfos: UwiInfo[], isEmpty?: boolean) => {
    // loop and check for local errors
    const uwis = toUwis(uwiInfos)
    uwiInfos.forEach((uwiInfo) => {
      const { uwi } = uwiInfo
      if (isEmpty === true && uwi.trim().length === 0) {
        uwiInfo.status = 'UWI is required'
      } else if (uwi.length > 0) {
        const status = validateUwiString(uwi)
        if (status != null) {
          uwiInfo.status = status
        } else {
          const isDuplicate = hasDuplicate(uwis, uwi)
          if (isDuplicate) {
            uwiInfo.status = 'Duplicate UWI'
          } else if (uwiInfo.status === 'Duplicate UWI') {
            uwiInfo.status = undefined
          }
        }
      }
    })
  }

  const validateUwisRemotely = async (uwiInfos: UwiInfo[]) => {
    // get the statuses of all remaining UWIs from CORP QRY
    const statelessUwis = uwiInfos
      .filter((uwiInfo) => uwiInfo.uwi.length > 0 && uwiInfo.status == null)
      .map((uwiInfo) => uwiInfo.uwi)
    if (statelessUwis.length > 0) {
      await validateUwisApi({ variables: { uwis: statelessUwis } })
    }
  }

  const showInvalidUwisPopup = (): void => {
    showErrorModal(
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          Only valid UWIs are allowed. Please remove the following invalid UWIs to proceed.
        </div>
        {/* @ts-ignore */}
        <Table
          size="small"
          pagination={false}
          style={{ fontSize: 12 }}
          columns={[
            {
              title: '',
              dataIndex: 'itemNum',
              key: 'itemNum',
            },
            {
              title: 'UWI',
              dataIndex: 'uwi',
              key: 'uwi',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
            },
          ]}
          dataSource={uwiInfos
            .filter((uwiInfo) => uwiInfo.status != null)
            .map((uwiInfo, idx) => {
              const { uwi, status } = uwiInfo
              return {
                key: idx,
                itemNum: idx + 1,
                uwi,
                status,
              }
            })}
        />
      </div>,
      'Invalid UWIs',
      () => {
        focusOnUwiList()
      }
    )
  }

  const focusOnUwiList = (): void => {
    let idx = 0
    for (idx = 0; idx < uwiInfos.length; idx++) {
      if (uwiInfos[idx].status != null) {
        break
      }
    }
    const uwiListInstance = form.getFieldInstance(['uwiList', idx])
    const inputEls = uwiListInstance?.getElementsByTagName('input')
    if (inputEls && inputEls.length === 1) {
      const inputEl = inputEls[0]
      window.setTimeout(() => {
        inputEl.focus()
      }, 500)
    }
  }

  // #endregion

  // renderer helper variables
  const isLoading = validateUwisLoading || createOrderLoading
  const isCreditCard = paymentOption === 1
  const isRemoveButtonVisible = uwiInfos.length > 1
  const isAddButtonVisible = uwiInfos.length < MAX_UWIS_ALLOWED
  const reportsCount = uwiInfos.filter((uwiInfo) => uwiInfo.uwi.trim().length > 0).length

  return (
    <>
      <Spin size="large" spinning={isLoading}>
        <OrderFormMain
          form={form}
          onFinish={formFinishHandler}
          onFinishFailed={formFinishFailedHandler}
          layout={'vertical'}
          validateMessages={validateMessages}
          scrollToFirstError={'smooth' as any}
        >
          <SubmissionDate />
          <OrderFormSection title={'Customer Information'}>
            <OrderFormRow>
              <Form.Item
                name="CompanyName"
                label="Company Name"
                rules={Validators.isRequired}
                colon={false}
              >
                <Input />
              </Form.Item>
            </OrderFormRow>
            <OrderFormRow>
              <Form.Item
                name="ContactName"
                label="Contact Name"
                rules={Validators.isRequired}
                colon={false}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="ContactEmail"
                label="Contact Email"
                rules={Validators.isRequiredEmail}
                colon={false}
              >
                <Input />
              </Form.Item>
            </OrderFormRow>
            <OrderFormRow>
              <Form.Item
                name="PhoneNumber"
                label="Phone Number"
                validateTrigger="onChange"
                rules={Validators.isRequiredPhoneNumber}
                colon={false}
              >
                <Input placeholder="xxx-xxx-xxxx" />
              </Form.Item>
              <Form.Item name="PoNumber" label="P.O. Number" colon={false}>
                <Input />
              </Form.Item>
            </OrderFormRow>
          </OrderFormSection>
          <OrderFormSection
            title={'Payment Information'}
            subtitle={
              <div>
                <div>Please specify payment method.</div>
                <div>
                  <strong>Note: All orders require advance payment.</strong>
                </div>
              </div>
            }
          >
            <OrderFormPaymentRow $isCreditCard={isCreditCard}>
              <Radio.Group
                value={paymentOption}
                onChange={(e: RadioChangeEvent) => {
                  setPaymentOption(e.target.value)
                }}
              >
                <Radio value={1}>
                  <div className="option-text">Credit Card</div>
                  <div className="credit-card-info">
                    <div>We will contact you for credit card information &nbsp;</div>
                    <div>
                      <img src={cc} />
                    </div>
                  </div>
                </Radio>
                <div className="spacer" />
                <Radio value={2}>
                  <div className="option-text">Bill Account</div>
                  <div className="bill-acount-info">
                    <Form.Item
                      name="BillAccount"
                      label="AER Account Number"
                      validateTrigger="onChange"
                      rules={Validators.isRequiredBillAccount(!isCreditCard)}
                      colon={false}
                    >
                      <Input
                        placeholder="X-0000 or 00-0000"
                        onChange={(e: any) => {
                          const uppercasedValue = e.target.value.toUpperCase()
                          form.setFieldsValue({ BillAccount: uppercasedValue })

                          setPaymentOption(2)
                        }}
                      />
                    </Form.Item>
                  </div>
                </Radio>
              </Radio.Group>
            </OrderFormPaymentRow>
          </OrderFormSection>
          <OrderFormSection
            title="Report Request"
            subtitle={
              <OrderFormRequestInfo>
                List the UWI for each report requested using the format specified below. Only valid
                UWIs will be processed. If there are multiple Event Sequences for the same UWI,
                please include only one in your order. The requested Tour Reports are saved by using
                the licence number(s) and the zero event UWI. If there are multiple current UWI
                options for a licence number and the UWI in the file is significantly different to
                how you ordered it, we will indicate the UWI used in the file naming.
              </OrderFormRequestInfo>
            }
          >
            <OrderFormRequestRow
              isAlwaysDouble={true}
              $isRemoveButtonVisible={isRemoveButtonVisible}
            >
              {uwiInfos.map((uwiInfo: UwiInfo, i: number) => {
                const isError = uwiInfo.status != null
                return (
                  <div key={`request-row-${i}`} className="request-row">
                    <Form.Item
                      name={['uwiList', i]}
                      label=""
                      colon={false}
                      validateStatus={isError ? 'error' : undefined}
                      help={isError ? uwiInfo.status : undefined}
                    >
                      <div className="uwi-item">
                        <div className="uwi-item-number">{`${i + 1}.`}</div>
                        <div className="uwi-item-input">
                          <Input
                            placeholder="00/00-00-000-00W0/0"
                            minLength={19}
                            onChange={(e) => uwiChangeHandler(e, i)}
                            value={uwiInfo.uwi}
                          />
                        </div>
                      </div>
                    </Form.Item>
                    <div className="uwi-btn-remove">
                      {isRemoveButtonVisible && (
                        <Button
                          type="text"
                          icon={<CloseCircleOutlined />}
                          onClick={() => removeReportHandler(i)}
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </OrderFormRequestRow>
            <OrderFormRequestFooter>
              <div className="add-button">
                {isAddButtonVisible && (
                  <Button type="primary" icon={<PlusCircleOutlined />} onClick={addReportHandler}>
                    Add Another Report
                  </Button>
                )}
              </div>
              <div className="uwis-count">
                <span>Number of Reports&nbsp;</span>
                <Input readOnly={true} value={reportsCount} />
              </div>
            </OrderFormRequestFooter>
          </OrderFormSection>

          <OrderFormFooter>
            <Button onClick={formResetHandler}>Reset</Button>
            <Button type="primary" size="large" onClick={formSubmitHandler}>
              Submit
            </Button>
          </OrderFormFooter>
        </OrderFormMain>
      </Spin>
    </>
  )
}

export default OrderForm
